import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Pickaxe, Zap, Shield, TrendingUp, Gem, Users, ChevronRight, ArrowRight } from "lucide-react";
import API from "../lib/api";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import AdCodeSlot from "../components/AdCodeSlot";
import PaymentProofsTicker from "../components/PaymentProofsTicker";
import PaymentProofsLanding from "../components/PaymentProofsLanding";

const FEATURES = [
  { icon: Gem, title: "Idle Extraction", desc: "Your workers dig 24/7 even while you sleep. Accumulate up to 12 hours of rewards automatically.", color: "#00F0FF" },
  { icon: Zap, title: "Worker System", desc: "Manage workers with energy, hunger & fatigue. Equip tools, boots, machines for bonus output.", color: "#FFD600" },
  { icon: TrendingUp, title: "6 Resource Tiers", desc: "From common Coal to legendary Mythril. Rarer materials fetch premium prices at the market.", color: "#B026FF" },
  { icon: Shield, title: "Real Economy", desc: "Convert game coins to withdrawable balance. Dynamic pricing, taxes, and admin-controlled rates.", color: "#10B981" },
  { icon: Users, title: "Daily Missions", desc: "Complete tasks for bonus coins. Login rewards, milestones, and challenges every day.", color: "#3B82F6" },
  { icon: Pickaxe, title: "Crypto Payouts", desc: "Request withdrawals to your wallet. Connect MetaMask, submit proof, get paid.", color: "#F59E0B" },
];

const STATS = [
  { value: "12H", label: "Max Idle Time" },
  { value: "6", label: "Resource Tiers" },
  { value: "100", label: "Free Starting Coins" },
  { value: "24/7", label: "Always Earning" },
];

export default function LandingPage() {
  const [content, setContent] = useState([]);
  const [proofsPageOn, setProofsPageOn] = useState(false);
  const { siteTitle } = useSiteSettings();

  useEffect(() => {
    API.get("/content").then(r => setContent(r.data.sections)).catch(() => {});
    API.get("/payment-proofs").then(r => setProofsPageOn(!!r.data?.settings?.page_enabled)).catch(() => {});
  }, []);

  const hero = content.find(s => s.section_id === "hero" && s.visible);
  const steps = content.filter(s => s.section_id?.startsWith("step_") && s.visible).sort((a, b) => a.order - b.order);
  const stats = content.filter(s => s.section_id?.startsWith("stat_") && s.visible).sort((a, b) => a.order - b.order);
  const features = content.filter(s => s.section_id?.startsWith("feature_") && s.visible).sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-[#0B0E14] overflow-hidden" data-testid="landing-page">
      <PaymentProofsTicker />
      {/* Nav */}
      <nav className="glass-panel sticky top-0 w-full z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center">
              <Gem className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <span className="text-xl font-bold text-[#F8FAFC] tracking-tight">{siteTitle}</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-btn" className="px-5 py-2 text-sm font-medium text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
              Login
            </Link>
            <Link to="/register" data-testid="nav-register-btn" className="px-5 py-2.5 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 transition-all shadow-[0_0_15px_rgba(0,240,255,0.4)]">
              Play Now
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-28 pb-16 px-6">
        <div className="max-w-6xl mx-auto"><AdCodeSlot position="top" page="landing" /></div>
        <div className="absolute inset-0 overflow-hidden">
          <img src={hero?.image_url || "https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/9ff327805df8bb7007aaa05b60bb28f3aec867c046d8fea9b6579e84c4c6e114.png"}
            alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14]/50 via-[#0B0E14]/70 to-[#0B0E14]" />
        </div>
        <div className="relative max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12">
          <div className="flex-1 text-center lg:text-left">
            <p className="text-xs font-mono-game text-[#00F0FF] tracking-[0.3em] uppercase mb-4 glow-cyan" data-testid="hero-subtitle">
              Browser-Based Crypto Earning Game
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#F8FAFC] mb-6 leading-tight" data-testid="hero-title">
              {hero?.title?.split(".").map((part, i) => {
                if (i === 0) return <span key={i}>{part}.<br /></span>;
                if (i === 1) return <span key={i}>{part}.<br /></span>;
                return <span key={i} className="text-[#00F0FF] glow-cyan">{part}</span>;
              }) || <>Play. Earn.<br /><span className="text-[#00F0FF] glow-cyan">Dominate.</span></>}
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] max-w-xl mb-8 leading-relaxed">
              {hero?.description || "Deploy workers into the depths, extract rare crystals, and forge your crypto empire. Idle extraction lets you earn 24/7."}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 lg:justify-start justify-center">
              <Link to={hero?.button_link || "/register"} data-testid="hero-play-btn" className="px-8 py-4 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 transition-all shadow-[0_0_20px_rgba(0,240,255,0.5)] animate-pulse-glow flex items-center gap-2">
                {hero?.button_text || "Start Earning Free"} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="hero-login-btn" className="px-8 py-4 text-sm font-bold uppercase tracking-widest border border-[#FFD600] text-[#FFD600] rounded-lg hover:bg-[#FFD600]/10 transition-all">
                Login
              </Link>
            </div>
          </div>
          <div className="flex-shrink-0 w-72 h-72 lg:w-96 lg:h-96 relative">
            <div className="absolute inset-0 bg-[#00F0FF]/10 rounded-full blur-3xl animate-mining-pulse" />
            <img src="https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/8d6e3f7efeac3bec6e15aa0d3009ebcea428e7a2b007276162970a447bd2d82c.png"
              alt="Game Character" className="relative w-full h-full object-contain drop-shadow-[0_0_30px_rgba(0,240,255,0.3)]" data-testid="hero-character" />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-10 px-6 border-y border-[#2A3241]/50">
        <div className="max-w-5xl mx-auto"><AdCodeSlot position="inline" page="landing" /></div>
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {(stats.length > 0 ? stats : STATS).map((s, i) => (
            <div key={s.section_id || s.label || i} className="text-center">
              <p className="text-3xl font-black text-[#00F0FF] font-mono-game glow-cyan">{s.title || s.value}</p>
              <p className="text-xs text-[#64748B] mt-1 tracking-[0.15em] uppercase">{s.description || s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How to Earn - Steps */}
      {steps.length > 0 && (
        <section className="py-20 px-6" data-testid="how-to-earn-section">
          <div className="max-w-6xl mx-auto">
            <p className="text-xs font-mono-game text-[#FFD600] tracking-[0.3em] uppercase text-center mb-3 glow-gold">How It Works</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F8FAFC] text-center mb-4">
              Start Earning in Minutes
            </h2>
            <p className="text-[#94A3B8] text-center mb-16 max-w-lg mx-auto">Follow these simple steps to begin your crypto earning journey</p>

            <div className="space-y-16">
              {steps.map((step, i) => {
                const isEven = i % 2 === 1;
                return (
                  <div key={step.section_id} className={`flex flex-col ${isEven ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-8 lg:gap-16 animate-slide-up`} data-testid={`step-${step.section_id}`} style={{ animationDelay: `${i * 100}ms` }}>
                    <div className="flex-1 relative group">
                      <div className="absolute -inset-2 bg-gradient-to-r from-[#00F0FF]/20 to-[#B026FF]/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative overflow-hidden rounded-2xl border border-[#2A3241]/40 group-hover:border-[#00F0FF]/30 transition-all">
                        <img src={step.image_url} alt={step.title} className="w-full h-64 sm:h-80 object-cover transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14]/60 to-transparent" />
                      </div>
                    </div>
                    <div className="flex-1 text-center lg:text-left">
                      <div className="inline-flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center">
                          <span className="font-mono-game text-sm font-bold text-[#00F0FF]">{i + 1}</span>
                        </div>
                        <div className="h-px w-12 bg-gradient-to-r from-[#00F0FF]/50 to-transparent" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-[#F8FAFC] mb-3">{step.title}</h3>
                      <p className="text-[#94A3B8] leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Features Grid */}
      <section className="py-20 px-6 bg-[#131722]/30">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-mono-game text-[#B026FF] tracking-[0.3em] uppercase text-center mb-3 glow-purple">Game Features</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F8FAFC] text-center mb-16">
            Deep Mechanics, Real Rewards
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(features.length > 0 ? features : FEATURES).map((f, i) => {
              const title = f.title;
              const desc = f.description || f.desc;
              const color = f.button_text || f.color || "#00F0FF";
              const Icon = FEATURES[i]?.icon || Gem;
              return (
              <div key={f.section_id || i} className="p-6 bg-[#131722] rounded-xl border border-[#2A3241]/40 hover:border-opacity-100 transition-all duration-300 hover:-translate-y-1 group">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${color}15` }}>
                  <Icon className="w-6 h-6" style={{ color }} />
                </div>
                <h3 className="text-lg font-bold text-[#F8FAFC] mb-2">{title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{desc}</p>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto relative">
          <div className="absolute inset-0 overflow-hidden rounded-2xl">
            <img src="https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/9ff327805df8bb7007aaa05b60bb28f3aec867c046d8fea9b6579e84c4c6e114.png"
              alt="" className="w-full h-full object-cover opacity-15" />
          </div>
          <div className="relative bg-[#131722]/80 backdrop-blur-xl rounded-2xl p-12 border border-[#2A3241]/40 text-center">
            <div className="inline-block mb-6">
              <img src="https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/5cb240ec3a39fda95f5b432009980ba2e75e1125dccb7baa6809c938eb01ca99.png"
                alt="Rewards" className="w-24 h-24 object-contain" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#F8FAFC] mb-4">
              Ready to Start Earning?
            </h2>
            <p className="text-[#94A3B8] mb-8 max-w-md mx-auto">Join now and get 100 free coins to begin. No credit card, no downloads, just pure earning.</p>
            <Link to="/register" data-testid="cta-register-btn" className="inline-flex items-center gap-2 px-8 py-4 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 shadow-[0_0_20px_rgba(0,240,255,0.5)]">
              Create Free Account <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Payment proofs landing surfaces (counter + recent payouts) */}
      <PaymentProofsLanding />

      {/* Footer */}
      <div className="max-w-6xl mx-auto px-6"><AdCodeSlot position="bottom" page="landing" /></div>
      <footer className="py-8 px-6 border-t border-[#2A3241]/50 text-center">
        <p className="text-xs text-[#64748B]">
          {siteTitle} &middot; Browser-Based Crypto Earning Game
          {proofsPageOn && (<>&nbsp;&middot;&nbsp;<Link to="/payment-proofs" className="hover:text-[#94A3B8]" data-testid="landing-footer-proofs-link">Payment Proofs</Link></>)}
        </p>
      </footer>
    </div>
  );
}
